import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Domain as DomainIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import {
  MAILBOX_ORDER_STATUS_CANCELLED,
  MAILBOX_ORDER_STATUS_FULFILLED,
  MAILBOX_ORDER_STATUS_PAID,
  MAILBOX_ORDER_STATUS_PENDING,
} from "../../constants";
import { hostname, parseError } from "../../utils";

interface RegistrantInfo {
  email: string;
  organizationName: string;
  firstName: string;
  lastName: string;
  phone: string;
  jobTitle: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface MailboxOrder {
  id: string;
  uid: string;
  companyId: string;
  companyName: string | null;
  userEmail: string | null;
  userName: string | null;
  orderDetails: {
    domains: Array<{
      domain: string;
      mailboxes: Array<{
        firstName: string;
        lastName: string;
        email: string;
        employeeId?: string;
      }>;
    }>;
  };
  domainCount: number;
  mailboxCount: number;
  domainPriceEach: number;
  mailboxPriceEach: number;
  totalPrice: number;
  currency: string;
  status: string;
  referenceDomain: string | null;
  contactEmail: string | null;
  contactName: string | null;
  registrantInfo: RegistrantInfo | null;
  fulfilledDate: string | null;
  fulfilledBy: string | null;
  fulfillmentNotes: string | null;
  adminNotifiedDate: string | null;
  created_date: string;
  updated_date: string;
}

interface MailboxOrderManagementProps {
  getToken: () => Promise<string>;
}

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: MAILBOX_ORDER_STATUS_PENDING, label: "Pending" },
  { value: MAILBOX_ORDER_STATUS_PAID, label: "Paid" },
  { value: MAILBOX_ORDER_STATUS_FULFILLED, label: "Fulfilled" },
  { value: MAILBOX_ORDER_STATUS_CANCELLED, label: "Cancelled" },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case MAILBOX_ORDER_STATUS_PENDING:
      return "warning";
    case MAILBOX_ORDER_STATUS_PAID:
      return "info";
    case MAILBOX_ORDER_STATUS_FULFILLED:
      return "primary";
    case MAILBOX_ORDER_STATUS_CANCELLED:
      return "error";
    default:
      return "default";
  }
};

const MailboxOrderManagement: React.FC<MailboxOrderManagementProps> = ({
  getToken,
}) => {
  const [orders, setOrders] = useState<MailboxOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Copy ID feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Order details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<MailboxOrder | null>(null);

  // Fulfill dialog
  const [fulfillDialogOpen, setFulfillDialogOpen] = useState(false);
  const [fulfillOrder, setFulfillOrder] = useState<MailboxOrder | null>(null);
  const [fulfillmentNotes, setFulfillmentNotes] = useState("");
  const [fulfilling, setFulfilling] = useState(false);

  const fetchOrders = async (search?: string, status?: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params: Record<string, string | number> = {
        page: page + 1,
        pageSize: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }
      if (status) {
        params.status = status;
      }

      const response = await axios.get(`${hostname}/mailbox-orders/admin`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrders(response.data.items);
      setTotalCount(response.data.total);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(searchQuery, statusFilter);
  }, [page, rowsPerPage, statusFilter]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchOrders(value, statusFilter);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return moment(dateString).format("MMM D, YYYY h:mm A");
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewDetails = (order: MailboxOrder) => {
    setSelectedOrder(order);
    setDetailsDialogOpen(true);
  };

  const handleOpenFulfillDialog = (order: MailboxOrder) => {
    setFulfillOrder(order);
    setFulfillmentNotes("");
    setFulfillDialogOpen(true);
  };

  const handleFulfillOrder = async () => {
    if (!fulfillOrder) return;

    setFulfilling(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/mailbox-orders/admin/${fulfillOrder.id}/fulfill`,
        { fulfillmentNotes: fulfillmentNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess("Order marked as fulfilled");
      setFulfillDialogOpen(false);
      fetchOrders(searchQuery, statusFilter);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setFulfilling(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Mailbox Orders
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => fetchOrders(searchQuery, statusFilter)}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search by company, email, domain..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon fontSize="small" />
              </InputAdornment>
            }
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Orders Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Domains</TableCell>
                <TableCell>Mailboxes</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Tooltip
                      title={copiedId === order.id ? "Copied!" : "Click to copy"}
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() => handleCopyId(order.id)}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            maxWidth: 80,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {order.id.slice(0, 8)}...
                        </Typography>
                        <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {order.companyName || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.companyId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {order.contactName || order.userName || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.contactEmail || order.userEmail || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<DomainIcon />}
                      label={order.domainCount}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<EmailIcon />}
                      label={order.mailboxCount}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      ${order.totalPrice.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      color={getStatusColor(order.status) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(order.created_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDetails(order)}
                      >
                        Details
                      </Button>
                      {(order.status === MAILBOX_ORDER_STATUS_PAID ||
                        order.status === MAILBOX_ORDER_STATUS_PENDING) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleOpenFulfillDialog(order)}
                        >
                          Fulfill
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No mailbox orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Order Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Order Details
            <IconButton
              onClick={() => setDetailsDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ pt: 1 }}>
              {/* Order Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order Information
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: "action.hover" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Order ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {selectedOrder.id}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Box>
                        <Chip
                          label={selectedOrder.status}
                          size="small"
                          color={getStatusColor(selectedOrder.status) as any}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Company
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.companyName || "Unknown"} (
                        {selectedOrder.companyId})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        User
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.userName || "-"} (
                        {selectedOrder.userEmail || selectedOrder.uid})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contact
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.contactName || "-"} (
                        {selectedOrder.contactEmail || "-"})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Reference Domain
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.referenceDomain || "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(selectedOrder.created_date)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Price
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        ${selectedOrder.totalPrice.toFixed(2)}{" "}
                        {selectedOrder.currency}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Fulfillment Info */}
              {selectedOrder.fulfilledDate && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Fulfillment Information
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: "action.hover" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Fulfilled Date
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(selectedOrder.fulfilledDate)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Fulfilled By
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.fulfilledBy || "-"}
                        </Typography>
                      </Box>
                      {selectedOrder.fulfillmentNotes && (
                        <Box sx={{ gridColumn: "span 2" }}>
                          <Typography variant="caption" color="text.secondary">
                            Notes
                          </Typography>
                          <Typography variant="body2">
                            {selectedOrder.fulfillmentNotes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>
              )}

              {/* Registrant Information */}
              {selectedOrder.registrantInfo && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Domain Registrant Information
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: "action.hover" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.email}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Organization
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.organizationName}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.firstName}{" "}
                          {selectedOrder.registrantInfo.lastName}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.phone}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Job Title
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.jobTitle}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Country
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.country}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: "span 2" }}>
                        <Typography variant="caption" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.registrantInfo.address1}
                          {selectedOrder.registrantInfo.address2 && (
                            <>, {selectedOrder.registrantInfo.address2}</>
                          )}
                          <br />
                          {selectedOrder.registrantInfo.city},{" "}
                          {selectedOrder.registrantInfo.state}{" "}
                          {selectedOrder.registrantInfo.postalCode}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}

              {/* Domains and Mailboxes */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Domains & Mailboxes ({selectedOrder.domainCount} domains,{" "}
                  {selectedOrder.mailboxCount} mailboxes)
                </Typography>
                {selectedOrder.orderDetails.domains.map((domain, idx) => (
                  <Accordion key={idx} defaultExpanded={idx === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <DomainIcon color="action" />
                        <Typography fontWeight={500}>{domain.domain}</Typography>
                        <Chip
                          label={`${domain.mailboxes.length} mailbox${
                            domain.mailboxes.length !== 1 ? "es" : ""
                          }`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Employee ID</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {domain.mailboxes.map((mailbox, midx) => (
                            <TableRow key={midx}>
                              <TableCell>
                                {mailbox.firstName} {mailbox.lastName}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: "monospace" }}
                                >
                                  {mailbox.email}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {mailbox.employeeId || "-"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedOrder &&
            (selectedOrder.status === MAILBOX_ORDER_STATUS_PAID ||
              selectedOrder.status === MAILBOX_ORDER_STATUS_PENDING) && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleOpenFulfillDialog(selectedOrder);
                }}
              >
                Mark as Fulfilled
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* Fulfill Order Dialog */}
      <Dialog
        open={fulfillDialogOpen}
        onClose={() => setFulfillDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Mark Order as Fulfilled
            <IconButton
              onClick={() => setFulfillDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {fulfillOrder && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will mark the order as fulfilled. Make sure all domains and
                mailboxes have been set up before proceeding.
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Order: {fulfillOrder.id}
                </Typography>
                <Typography variant="body2">
                  {fulfillOrder.domainCount} domain(s),{" "}
                  {fulfillOrder.mailboxCount} mailbox(es)
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Fulfillment Notes (optional)"
                multiline
                rows={3}
                value={fulfillmentNotes}
                onChange={(e) => setFulfillmentNotes(e.target.value)}
                placeholder="Any notes about the fulfillment..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setFulfillDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleFulfillOrder}
            loading={fulfilling}
            startIcon={<CheckCircleIcon />}
          >
            Confirm Fulfillment
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MailboxOrderManagement;
